/**
 * Quotes, versions and exports (API `quotes/*`).
 */
import { download, request, settled, upload } from "./http";

class QuotesService {
  /** `{ superuser, permissions: [keys] }` of the logged user. */
  myPermissions() {
    return request("get", "me/permissions");
  }

  /** What the hub opens with: `{ quotes, inbox, agreements, settings, recent }` (blocks null without the tab). */
  dashboard() {
    return request("get", "dashboard");
  }

  /** Service types the user may create quotes of. */
  services() {
    return request("get", "services");
  }

  /** Paginated list; filters: state, service_type_id, client_no, search, page, per_page. */
  list(params) {
    return request("get", "", { params });
  }

  /** `{ status, data }`: 201 with the quote, 403 `service_unavailable`, 422 `client_unknown`. */
  create(payload) {
    return settled("post", "", { data: payload });
  }

  get(id) {
    return request("get", `${id}`);
  }

  rename(id, payload) {
    return settled("patch", `${id}`, { data: payload });
  }

  /** `{ status, data }`: 409 when a version is no longer a draft. */
  remove(id) {
    return settled("delete", `${id}`);
  }

  /** The editor payload: quote, version, questionnaire, result, totals, situation, history, permissions. */
  version(id, number) {
    return request("get", `${id}/versions/${number}`);
  }

  /** Pure calculation of unsaved answers; `{ status, data }` to keep validation errors. */
  calculate(id, number, payload) {
    return settled("post", `${id}/versions/${number}/calculate`, { data: payload });
  }

  /** `{ status, data }`: 409 `version_frozen`, 403 on a section the user may not edit. */
  save(id, number, payload) {
    return settled("put", `${id}/versions/${number}`, { data: payload });
  }

  submit(id, number) {
    return settled("post", `${id}/versions/${number}/submit`);
  }

  close(id, number) {
    return settled("post", `${id}/versions/${number}/close`);
  }

  /** `{ decision: "approve" | "reject", reason? }` */
  decide(id, number, payload) {
    return settled("post", `${id}/versions/${number}/decide`, { data: payload });
  }

  reopen(id, number) {
    return settled("post", `${id}/versions/${number}/reopen`);
  }

  /** `{ from_version? }`; 201 with the new version. */
  newVersion(id, payload = {}) {
    return settled("post", `${id}/versions`, { data: payload });
  }

  removeVersion(id, number) {
    return settled("delete", `${id}/versions/${number}`);
  }

  history(id, number) {
    return request("get", `${id}/versions/${number}/history`);
  }

  /** `{ blob, filename }` of the proposal PDF, named after the proposal, or null. */
  pdf(id, number) {
    return download("get", `${id}/versions/${number}/pdf`);
  }

  /** `{ blob, filename }` of the editable PowerPoint, or null. */
  pptx(id, number) {
    return download("get", `${id}/versions/${number}/pptx`);
  }

  /** HTML preview of the proposal (string) or null. */
  proposalHtml(id, number) {
    return request("get", `${id}/versions/${number}/proposal`, { params: { format: "html" } });
  }

  /** `{ blob, filename }` of the Excel; `variant` internal | client | detailed. */
  excel(id, number, variant) {
    return download("get", `${id}/versions/${number}/excel`, { params: { variant } });
  }

  /** `{ blob, filename }`: one Excel per item in a ZIP (a single file when one came out), or null. */
  exportBatch(items, variant, groupId = null) {
    return download("post", "export-batch", { data: { items, variant, group_id: groupId } });
  }

  /** `{ outcome: sold | not_sold | cancel | revalidate, sla_*?, confirm_sla? }` → the editor payload. */
  outcome(id, number, payload) {
    return settled("post", `${id}/versions/${number}/outcome`, { data: payload });
  }

  /** What the sale would send to the ERP, what blocks it, the ERP's lists; `{ classification?, area? }`. */
  salePreview(id, number, params) {
    return request("get", `${id}/versions/${number}/sale`, { params });
  }

  /** Sells the version in the ERP; 409 not_sold, 422 sale_blocked (with `problems`), the ERP's own codes. */
  sell(id, number, payload) {
    return settled("post", `${id}/versions/${number}/sale`, { data: payload });
  }

  /** `{ kind: labour | supplier_service, description, quantity, unit?, unit_cost?, margin?, file? }`. */
  addExtra(id, number, payload) {
    return settled("post", `${id}/versions/${number}/extras`, { data: payload });
  }

  removeExtra(id, number, extraId) {
    return settled("delete", `${id}/versions/${number}/extras/${encodeURIComponent(extraId)}`);
  }

  /** `{ branch_id?, force?, auto? }` → the editor payload with the travel km written (`auto`: the editor's first request, cached route kept); 422 no_site/no_coordinates/branch_unknown. */
  travel(id, number, payload) {
    return settled("post", `${id}/versions/${number}/travel`, { data: payload });
  }

  /** The system scheme: `{ scheme, edits, icons, description, editable, image_available, version }`. */
  scheme(id, number) {
    return request("get", `${id}/versions/${number}/scheme`);
  }

  /** `{ edits }` → the scheme payload again; 409 version_frozen, 403 without edit. */
  saveScheme(id, number, edits) {
    return settled("put", `${id}/versions/${number}/scheme`, { data: { edits } });
  }

  /** `{ blob, filename }` of the scheme as SVG, or null. */
  schemeSvg(id, number) {
    return download("get", `${id}/versions/${number}/scheme.svg`);
  }

  /** The active branches (`{ id, name, main, has_coordinates }`), to choose where a new quote leaves from. */
  branches() {
    return request("get", "branches");
  }

  /** The quote's files (supplier budgets). */
  attachments(id) {
    return request("get", `${id}/attachments`);
  }

  /** Multipart upload of one file to a draft version; 201 with the list, 422 `file_type`, 409 frozen. */
  uploadAttachment(id, number, file) {
    const form = new FormData();
    form.append("file", file, file.name);
    return upload(`${id}/versions/${number}/attachments`, form);
  }

  /** `{ blob, filename }` of a stored file, or null. */
  attachment(id, attachmentId) {
    return download("get", `${id}/attachments/${attachmentId}`);
  }

  removeAttachment(id, attachmentId) {
    return settled("delete", `${id}/attachments/${attachmentId}`);
  }

  /** The assistant reads the supplier's file into lines: 202 with the list; 409 request_pending, 422 file_without_text, 503. */
  readAttachment(id, number, attachmentId) {
    return settled("post", `${id}/versions/${number}/attachments/${attachmentId}/read`);
  }

  /** Where the reading stands: `{ pending, proposal_status, lines[], rejected, proposal_warning, default_margin, can_edit_margin, error }`. */
  attachmentProposal(id, attachmentId) {
    return request("get", `${id}/attachments/${attachmentId}/proposal`);
  }

  /** `{ lines: [{ index?, description, quantity, unit, unit_cost }], margin? }` → the editor payload. */
  applyAttachment(id, number, attachmentId, payload) {
    return settled("post", `${id}/versions/${number}/attachments/${attachmentId}/apply`, { data: payload });
  }

  discardAttachment(id, number, attachmentId) {
    return settled("post", `${id}/versions/${number}/attachments/${attachmentId}/discard`);
  }

  /* ------------------------------------------------------------ assistant */

  /** `{ enabled, available, ready, reason, model, thread, proposal, pending, assumptions, has_report, files }`. */
  assistant(id, number) {
    return request("get", `${id}/versions/${number}/assistant`);
  }

  /** `{ report? | file_id? | request? }` → 202 with the assistant block; 409 request_pending, 503 unavailable. */
  requestAssistant(id, number, payload) {
    return settled("post", `${id}/versions/${number}/assistant/analyze`, { data: payload });
  }

  /** `{ answers: [{ key?, question, answer }] }` → 202 with the assistant block. */
  answerAssistant(id, number, answers) {
    return settled("post", `${id}/versions/${number}/assistant/answers`, { data: { answers } });
  }

  /** `{ keys: [...] }` → the full editor payload, recalculated; 409 no_proposal. */
  applyAssistant(id, number, keys) {
    return settled("post", `${id}/versions/${number}/assistant/apply`, { data: { keys } });
  }

  discardAssistant(id, number) {
    return settled("post", `${id}/versions/${number}/assistant/discard`);
  }

  removeAssumption(id, number, index) {
    return settled("delete", `${id}/versions/${number}/assumptions/${index}`);
  }

  /** The list as a tree: clients › sites › quotes › versions, KPIs, groups, pending. */
  tree(params) {
    return request("get", "tree", { params });
  }

  groups() {
    return request("get", "groups");
  }

  /** `{ name, quote_ids }`; 201 with the group. */
  createGroup(payload) {
    return settled("post", "groups", { data: payload });
  }

  updateGroup(id, payload) {
    return settled("put", `groups/${id}`, { data: payload });
  }

  removeGroup(id) {
    return settled("delete", `groups/${id}`);
  }
}

export default new QuotesService();
